import React from 'react';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiDivider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import { AvenyThemeSpacing } from '../../ThemeProvider/AvenyTheme';

interface IProps {
  open: boolean;
  close: () => void;
  toolbar?: React.ReactNode;
  onTransitionEnd?: () => void;
  contentRef?: React.RefObject<HTMLDivElement | null>;
  hideToolbarDivider?: boolean;
  fixed?: boolean;
  children?: React.ReactNode;
}

export const drawerWidth = 304;

const getDrawerStateSx = (theme: Theme, open: boolean) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  ...(open
    ? {
        width: drawerWidth,
        boxShadow: theme.shadows[2],
      }
    : {
        width: AvenyThemeSpacing * 8,
      }),
});

const drawerSx = {
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '@media print': {
    display: 'none',
  },
};

const paperSx = {
  zIndex: 0,
  overflowX: 'hidden',
  position: 'inherit',
  backgroundColor: 'common.white',
};

const contentSx = {
  width: drawerWidth,
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const contentWithoutToolbarSx = {
  overflowY: 'auto',
  height: '100%',
};

const hiddenSx = {
  opacity: 0,
};

/**
 * Ett specialfall av menyn, där menyn visas med ett minimalt utseende initialt.
Oftast handlar det om en kolumn av ikoner.
När den fälls ut, så kan ytterligare text och undermenyer visas.
 */
export const EDMiniDrawer = ({
  open,
  toolbar,
  children,
  contentRef,
  fixed,
  hideToolbarDivider,
  onTransitionEnd,
}: IProps) => {
  const drawerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const drawerElement = drawerRef.current;

    if (!drawerElement) {
      return undefined;
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === drawerElement && event.propertyName === 'width') {
        onTransitionEnd?.();
      }
    };

    drawerElement.addEventListener('transitionend', handleTransitionEnd, true);

    return () => {
      drawerElement.removeEventListener(
        'transitionend',
        handleTransitionEnd,
        true
      );
    };
  }, [onTransitionEnd]);

  const setDrawerRef = React.useCallback((ref: HTMLElement | null) => {
    drawerRef.current = ref;
  }, []);

  return (
    <MuiDrawer
      ref={setDrawerRef}
      variant={'permanent'}
      sx={theme => ({
        ...drawerSx,
        ...(fixed ? { position: 'fixed' } : {}),
        ...getDrawerStateSx(theme, open),
        ...(open ? { zIndex: 100 } : {}),
      })}
      slotProps={{
        paper: {
          sx: theme => ({
            top: theme.spacing(7),
            ...paperSx,
          }),
        },
      }}
      open={open}
    >
      <Box sx={contentSx}>
        {toolbar}
        <MuiDivider sx={hideToolbarDivider ? hiddenSx : undefined} />
        <Box sx={contentWithoutToolbarSx} ref={contentRef}>
          {children}
        </Box>
      </Box>
    </MuiDrawer>
  );
};

export default EDMiniDrawer;
